<template>
  <div class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
    <div class="sidebar-header">
      <div class="profile-info" v-if="currentProfile">
        <ion-icon :icon="personOutline" class="profile-icon"></ion-icon>
        <div class="profile-details">
          <h3>{{ currentProfile.first_name }} {{ currentProfile.last_name }}</h3>
          <p>Budget Dashboard</p>
        </div>
      </div>
    </div>

    <div class="sidebar-content">
      <div class="nav-section" :class="{ 'collapsed': !navigationExpanded }">
        <div class="nav-section-header" @click="toggleNavigation">
          <h4 class="nav-section-title">Navigation</h4>
          <ion-icon 
            :icon="chevronDownOutline" 
            class="nav-section-toggle"
            :class="{ 'rotated': navigationExpanded }"
          ></ion-icon>
        </div>
        <div v-if="navigationExpanded" class="nav-items">
          <div class="nav-item" @click="goToDashboard" :class="{ active: $route.name === 'Dashboard' }">
            <ion-icon :icon="statsChartOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">Dashboard</span>
          </div>
          
          <div class="nav-item" @click="goToCalendar" :class="{ active: $route.name === 'Calendar' }">
            <ion-icon :icon="calendarOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">Calendar</span>
          </div>
          
          <div class="nav-item" @click="addTransaction" :class="{ active: $route.name === 'Transaction' }">
            <ion-icon :icon="addOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">Add Transaction</span>
          </div>
          
          <div class="nav-item" @click="goToFeedback" :class="{ active: $route.name === 'Feedback' }">
            <ion-icon :icon="chatbubbleOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">Feedback</span>
          </div>
        </div>
      </div>

      <div class="nav-section" :class="{ 'collapsed': !profilesExpanded }">
        <div class="nav-section-header" @click="toggleProfiles">
          <h4 class="nav-section-title">Profiles</h4>
          <ion-icon 
            :icon="chevronDownOutline" 
            class="nav-section-toggle"
            :class="{ 'rotated': profilesExpanded }"
          ></ion-icon>
        </div>
        <div v-if="profilesExpanded" class="nav-items">
          <div class="profile-list">
            <div 
              v-for="profile in sortedProfiles" 
              :key="profile.id || profile._id"
              class="profile-item"
              :class="{ 'active-profile': (profile.id === currentProfile?.id || profile._id === currentProfile?.id) }"
              @click="selectProfile(profile)"
            >
              <ion-icon :icon="personOutline" class="profile-item-icon"></ion-icon>
              <span class="profile-item-label">{{ profile.first_name }} {{ profile.last_name }}</span>
            </div>
            <div v-if="sortedProfiles.length === 0" class="no-profiles">
              <span class="no-profiles-text">No profiles available</span>
            </div>
          </div>
        </div>
      </div>

      <div class="nav-section" :class="{ 'collapsed': !settingsExpanded }">
        <div class="nav-section-header" @click="toggleSettings">
          <h4 class="nav-section-title">Settings</h4>
          <ion-icon 
            :icon="chevronDownOutline" 
            class="nav-section-toggle"
            :class="{ 'rotated': settingsExpanded }"
          ></ion-icon>
        </div>
        <div v-if="settingsExpanded" class="nav-items">
          <div class="nav-item" @click="goToUserSettings" :class="{ active: $route.name === 'UserSettings' }">
            <ion-icon :icon="personOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">User Settings</span>
          </div>
          
          <div class="nav-item" @click="goToProfileSettings" :class="{ active: $route.name === 'ProfileSettings' }">
            <ion-icon :icon="peopleOutline" class="nav-icon"></ion-icon>
            <span class="nav-label">Profile Settings</span>
          </div>
        </div>
      </div>

      <div class="sidebar-footer">
        <div class="nav-item logout-item" @click="handleLogout">
          <ion-icon :icon="logOutOutline" class="nav-icon"></ion-icon>
          <span class="nav-label">Sign Out</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { IonButton, IonIcon } from '@ionic/vue';
import { 
  statsChartOutline, 
  calendarOutline, 
  logOutOutline, 
  personOutline,
  peopleOutline,
  addOutline,
  chevronDownOutline,
  chatbubbleOutline
} from 'ionicons/icons';
import { useProfileStore } from '@/store/profile';
import { useCalendarStore } from '@/store/calendar';
import { mapState, mapActions } from 'pinia';

export default {
  name: 'SidebarNavigation',
  components: {
    IonButton,
    IonIcon
  },
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const profileStore = useProfileStore();
    const calendarStore = useCalendarStore();
    return { profileStore, calendarStore };
  },
  data() {
    return {
      statsChartOutline,
      calendarOutline,
      logOutOutline,
      personOutline,
      peopleOutline,
      addOutline,
      chevronDownOutline,
      chatbubbleOutline,
      navigationExpanded: true,
      profilesExpanded: false,
      settingsExpanded: false
    };
  },
  computed: {
    ...mapState(useProfileStore, ['currentProfile', 'profiles']),
    sortedProfiles() {
      if (!this.profiles) return [];
      
      // Create a copy of the profiles array
      const sortedProfiles = [...this.profiles];
      
      // Sort profiles to put the current profile first
      if (this.currentProfile) {
        sortedProfiles.sort((a, b) => {
          if (a.id === this.currentProfile.id || a._id === this.currentProfile.id) return -1;
          if (b.id === this.currentProfile.id || b._id === this.currentProfile.id) return 1;
          return 0;
        });
      }
      
      return sortedProfiles;
    }
  },
  methods: {
    ...mapActions(useProfileStore, ['setCurrentProfile']),
    goToDashboard() {
      this.closeSidebarOnMobile();
      // Navigate to dashboard
      if (this.$route.path !== '/dashboard') {
        this.$router.push('/dashboard');
      }
    },
    closeSidebarOnMobile() {
      if (window.innerWidth <= 768) {
        const sidebarContainer = document.querySelector('.sidebar-container');
        if (sidebarContainer) {
          sidebarContainer.classList.remove('open');
        }
        document.body.classList.remove('sidebar-open');
        this.$emit('close-sidebar');
      }
    },
    goToCalendar() {
      this.closeSidebarOnMobile();
      if (!this.currentProfile) {
        console.warn('No current profile available for calendar navigation');
        return;
      }
      this.$router.push({
        path: '/calendar',
        query: { 
          profileID: this.currentProfile.id
        }
      });
    },
    addTransaction() {
      this.closeSidebarOnMobile();
      if (!this.currentProfile) {
        console.warn('No current profile available for adding transaction');
        return;
      }
      this.$router.push({
        path: '/transaction',
        query: { 
          profileID: this.currentProfile.id
        }
      });
    },
    goToUserSettings() {
      this.closeSidebarOnMobile();
      this.$router.push('/user-settings');
    },
    goToProfileSettings() {
      this.closeSidebarOnMobile();
      this.$router.push('/profile-settings');
    },
    goToFeedback() {
      this.closeSidebarOnMobile();
      this.$router.push('/feedback');
    },
    toggleNavigation() {
      this.navigationExpanded = !this.navigationExpanded;
    },
    toggleProfiles() {
      this.profilesExpanded = !this.profilesExpanded;
    },
    toggleSettings() {
      this.settingsExpanded = !this.settingsExpanded;
    },
    async selectProfile(profile) {
      try {
        console.log('Selecting profile:', profile);
        await this.profileStore.setCurrentProfile(profile);
        
        // Always navigate to dashboard to ensure fresh data loading
        if (this.$route.name === 'Dashboard') {
          // If already on dashboard, emit a custom event to trigger data refresh
          this.$eventBus.emit('profile-changed', profile);
          // Also force a router refresh
          this.$router.replace('/dashboard');
        } else {
          // Navigate to dashboard
          this.$router.push('/dashboard');
        }
      } catch (error) {
        console.error('Error selecting profile:', error);
      }
    },
    async handleLogout() {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('userID');
        await this.profileStore.resetCurrentProfile();
        await this.calendarStore.resetForNewUser();
      } catch (error) {
        console.error('Error during logout:', error);
        this.$router.push('/login');
      }
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
/* Sidebar-specific responsive behavior */
.sidebar-collapsed {
  width: 70px;
  min-width: 70px;
}

/* Sidebar-specific animations */
.nav-section-toggle.rotated {
  transform: rotate(180deg);
}
</style>
